import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { TenantService } from '../../../core/common/services/tenant/tenant.service';
import { LookupResource } from './lookup-resource.model';
import {
  LOOKUP_RESOURCE_ID_PLACEHOLDER,
  LOOKUP_RESOURCE_ROUTES,
  LookupResourceRouteConfig,
} from './lookup-resource-routes.constants';

/**
 * Resolves a `:control_name` reference from sibling form controls, additional
 * keys written on select (`{fieldKey}.{name}`), or the selected option itself.
 */
export type LookupResourceParamResolver = (controlName: string) => unknown;

/**
 * Builds details-page URLs for lookup fields from the backend-driven
 * `relation_resource` descriptor.
 *
 * `extra_params` values may be:
 * - a static literal (`7`)
 * - a dynamic `:control_name` reference resolved via `resolveParam`
 * - `null` (optional params are omitted from the URL)
 */
@Service()
export class LookupResourceNavigationService {
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);

  /**
   * Returns the absolute URL of the looked-up resource, or `null` when it is
   * not linkable — the resource is not clickable, its module is not supported
   * by this app yet, or a required route param cannot be resolved.
   */
  buildResourceUrl(
    resource: LookupResource | null | undefined,
    resourceId: string | number | null | undefined,
    resolveParam: LookupResourceParamResolver
  ): string | null {
    if (!resource?.is_clickable || !this.hasValue(resourceId)) {
      return null;
    }

    const routeConfig = LOOKUP_RESOURCE_ROUTES[resource.name];
    if (!routeConfig) {
      return null;
    }

    const segments: string[] = [];
    for (const segment of routeConfig.segments) {
      const resolved = this.resolvePlaceholder(
        segment,
        resource,
        resourceId,
        resolveParam
      );
      // An unresolved path param would build a URL that cannot match a route.
      if (!this.hasValue(resolved)) {
        return null;
      }
      segments.push(String(resolved));
    }

    const path = this.tenantService.tenantPath(segments.join('/'));
    return this.router.serializeUrl(
      this.router.createUrlTree([path], {
        queryParams: this.buildQueryParams(
          routeConfig,
          resource,
          resourceId,
          resolveParam
        ),
      })
    );
  }

  private buildQueryParams(
    routeConfig: LookupResourceRouteConfig,
    resource: LookupResource,
    resourceId: string | number,
    resolveParam: LookupResourceParamResolver
  ): Record<string, string> | undefined {
    if (!routeConfig.queryParams) {
      return undefined;
    }

    const resolved = Object.entries(routeConfig.queryParams).reduce<
      Record<string, string>
    >((accumulator, [key, placeholder]) => {
      const value = this.resolvePlaceholder(
        placeholder,
        resource,
        resourceId,
        resolveParam
      );
      // Optional query params are dropped rather than sent as "null".
      if (this.hasValue(value)) {
        accumulator[key] = String(value);
      }
      return accumulator;
    }, {});

    return Object.keys(resolved).length ? resolved : undefined;
  }

  private resolvePlaceholder(
    placeholder: string,
    resource: LookupResource,
    resourceId: string | number,
    resolveParam: LookupResourceParamResolver
  ): unknown {
    if (!placeholder.startsWith(':')) {
      return placeholder;
    }
    if (placeholder === LOOKUP_RESOURCE_ID_PLACEHOLDER) {
      return resourceId;
    }

    const rawValue = resource.extra_params?.[placeholder.slice(1)];
    return typeof rawValue === 'string' && rawValue.startsWith(':')
      ? resolveParam(rawValue.slice(1))
      : rawValue;
  }

  private hasValue(value: unknown): value is string | number {
    return value !== null && value !== undefined && value !== '';
  }
}
